import React, { FC, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAddTeam } from '../../../hooks/useAddTeam';
import { DepartmentsEntity } from '../../../hooks/useFulfillDepartments';
import { IParticipantsItem } from '../../../services/surveys';
import SearchDropdown, { ISearchDropdownItem } from '../../SearchDropdown/SearchDropdown';
import SearchInputHeader from '../../SearchDropdown/SearchInputHeader/SearchInputHeader';
import { IPostParticipantBodyPreTransform } from '../AddParticipantModal';
import {
  AdditionalLabel,
  AddParticipantFormCont,
  ControllerCont,
  FormPartCell,
  Label,
  OuterCellWrapper,
} from './AddParticipantForm.styles';
import participantSchemaBuilder from '@/helpers/participantSchemaBuilder';

const respondentsDefault = () => [
  {
    id: 'member',
    name: `${t('viewSurvey.reportsSetup.participants.additional.member')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.memberDescription')}`,
  },
  {
    id: 'leader',
    name: `${t('viewSurvey.reportsSetup.participants.additional.leader')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.leaderDescription')}`,
  },
  {
    id: 'superordinate',
    name: `${t('viewSurvey.reportsSetup.participants.additional.superordinate')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.superordinateDescription')}`,
  },
  {
    id: 'subordinate',
    name: `${t('viewSurvey.reportsSetup.participants.additional.subordinate')}`,
    description: `${t('viewSurvey.reportsSetup.participants.additional.subordinateDescription')}`,
  },
];

export interface ReconfiguredDepartmentEntity {
  id: string;
  name: string;
}

interface IAddParticipantForm {
  submit: (data: IPostParticipantBodyPreTransform) => void;
  baseLang: string;
  apiError?: { [key: string]: string };
  editData?: IParticipantsItem;
  surveyType: string;
  departments: DepartmentsEntity[];
  loadMoreDepartments?: () => void;
}

const AddParticipantForm: FC<IAddParticipantForm> = ({
  submit,
  baseLang,
  apiError,
  editData,
  surveyType,
  departments,
  loadMoreDepartments,
}) => {
  const isEffectSurvey = surveyType === 'EFFECT';
  const userLang = baseLang || 'en';
  const { langArrWithIcons, getLangObjectShort } = useAddTeam();
  const { t } = useTranslation();

  const { defaultValues, defaultSchema } = participantSchemaBuilder(
    isEffectSurvey,
    editData,
    getLangObjectShort,
    userLang,
    departments
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IPostParticipantBodyPreTransform>({
    defaultValues,
    resolver: yupResolver(
      defaultSchema as never
    ) as unknown as Resolver<IPostParticipantBodyPreTransform>,
  });

  const inputRef = useRef(null);
  return (
    <AddParticipantFormCont onSubmit={handleSubmit(submit)}>
      <OuterCellWrapper>
        <FormPartCell apiError={!!errors?.name?.message || !!apiError?.name}>
          <Label>{t('viewSurvey.participants.form.name')}</Label>
          <ControllerCont>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchInputHeader
                  width="303px"
                  noDrop
                  searchForList={onChange}
                  active={false}
                  placeholder={t('viewSurvey.participants.membersForm.name')}
                  inputRef={inputRef}
                  defaultValue={value}
                  error={errors?.name?.message || apiError?.name}
                />
              )}
              name="name"
            />
          </ControllerCont>
        </FormPartCell>
      </OuterCellWrapper>

      <OuterCellWrapper>
        <FormPartCell
          apiError={!!errors?.emailAddress?.message || !!apiError?.emailAddress}
          type="email"
        >
          <Label>{t('viewSurvey.participants.membersForm.email')}</Label>
          <ControllerCont>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchInputHeader
                  width="303px"
                  noDrop
                  searchForList={onChange}
                  active={false}
                  placeholder={t('viewSurvey.participants.membersForm.email')}
                  inputRef={inputRef}
                  defaultValue={value}
                  error={errors?.emailAddress?.message || apiError?.emailAddress}
                />
              )}
              name="emailAddress"
            />
          </ControllerCont>
        </FormPartCell>
      </OuterCellWrapper>

      <OuterCellWrapper>
        <FormPartCell apiError={!!errors?.phoneNo?.message || !!apiError?.phoneNo}>
          <Label>{t('viewSurvey.participants.form.phone')}</Label>
          <AdditionalLabel>{t('viewSurvey.participants.form.optional')}</AdditionalLabel>
          <ControllerCont>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchInputHeader
                  width="303px"
                  noDrop
                  searchForList={onChange}
                  active={false}
                  placeholder="(+__) __ - __ - __"
                  inputRef={inputRef}
                  defaultValue={value}
                  error={errors?.phoneNo?.message || apiError?.phoneNo}
                />
              )}
              name="phoneNo"
            />
          </ControllerCont>
        </FormPartCell>
      </OuterCellWrapper>

      <OuterCellWrapper>
        <FormPartCell>
          <Label>{t('viewSurvey.participants.form.lang')}</Label>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchDropdown
                items={langArrWithIcons as unknown as ISearchDropdownItem[]}
                placeholder={t('viewSurvey.participants.form.country')}
                selectEl={onChange}
                width="303px"
                activeEl={value as unknown as ISearchDropdownItem}
                selectFullObject
              />
            )}
            name="preferredLanguage"
          />
        </FormPartCell>
      </OuterCellWrapper>

      {isEffectSurvey ? (
        <>
          <OuterCellWrapper>
            <FormPartCell apiError={!!errors?.role?.message || !!apiError?.role}>
              <Label>{t('viewSurvey.participants.form.respondents')}</Label>
              <ControllerCont>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SearchDropdown
                      items={respondentsDefault()}
                      placeholder={t('viewSurvey.participants.form.respondents')}
                      selectEl={selectedId => {
                        const selectedItem = respondentsDefault().find(
                          item => item.name === selectedId
                        );

                        if (selectedItem) {
                          onChange(selectedItem.id);
                        }
                      }}
                      width="303px"
                      activeEl={respondentsDefault().find(item => item.id === value)}
                      selectFullObject={false}
                      error={errors?.role?.message || apiError?.role}
                    />
                  )}
                  name="role"
                />
              </ControllerCont>
            </FormPartCell>
          </OuterCellWrapper>

          <OuterCellWrapper>
            <FormPartCell apiError={!!errors?.department?.message || !!apiError?.department}>
              <Label>{t('viewSurvey.participants.form.departments')}</Label>
              <ControllerCont>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SearchDropdown
                      items={departments.map(
                        ({ id, name }) => ({ id, name } as ISearchDropdownItem)
                      )}
                      placeholder={t('viewSurvey.participants.form.departments')}
                      selectEl={onChange}
                      width="303px"
                      activeEl={value as unknown as ISearchDropdownItem}
                      selectFullObject
                      onScrollToBottom={loadMoreDepartments}
                      error={
                        errors?.department?.id?.message ||
                        (apiError?.department &&
                          t('viewSurvey.participants.modals.validations.departments'))
                      }
                    />
                  )}
                  name="department"
                />
              </ControllerCont>
            </FormPartCell>
          </OuterCellWrapper>
        </>
      ) : (
        <>
          <OuterCellWrapper>
            <FormPartCell apiError={!!errors?.team?.message || !!apiError?.team}>
              <Label>{t('viewSurvey.participants.form.team')}</Label>
              <ControllerCont>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SearchInputHeader
                      width="303px"
                      noDrop
                      searchForList={onChange}
                      active={false}
                      placeholder="Team"
                      inputRef={inputRef}
                      defaultValue={value}
                      error={errors?.team?.message || apiError?.team}
                    />
                  )}
                  name="team"
                />
              </ControllerCont>
            </FormPartCell>
          </OuterCellWrapper>

          <OuterCellWrapper>
            <FormPartCell apiError={!!errors?.leader?.message || !!apiError?.leader}>
              <Label>{t('viewSurvey.participants.form.leader')}</Label>
              <ControllerCont>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SearchInputHeader
                      width="303px"
                      noDrop
                      searchForList={onChange}
                      active={false}
                      placeholder={t('viewSurvey.participants.form.leader')}
                      inputRef={inputRef}
                      defaultValue={value}
                      error={errors?.leader?.message || apiError?.leader}
                    />
                  )}
                  name="leader"
                />
              </ControllerCont>
            </FormPartCell>
          </OuterCellWrapper>
        </>
      )}

      <button
        data-testid="submitAddParticipant"
        id="AddParticipantModalCallToAction"
        type="submit"
        hidden
      >
        callToAction
      </button>
    </AddParticipantFormCont>
  );
};

export default AddParticipantForm;
